import { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '@/lib/api';
import ProductCard from '@/components/ProductCard';
import { Search } from 'lucide-react';

const ALL_GENDERS = [
  { slug: 'ladies', name: 'Ladies' },
  { slug: 'gents', name: 'Gents' },
  { slug: 'unisex', name: 'Unisex' },
  { slug: 'kids', name: 'Kids' },
];

export default function Products() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [allProducts, setAllProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  const category = searchParams.get('category') || 'all';
  const subcategory = searchParams.get('subcategory') || 'all';
  const gender = searchParams.get('gender') || 'all';

  useEffect(() => {
    api.get('/categories').then((r) => setCategories(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    // Fetch by category + gender + search only (subcategory filter applied client-side for grouping)
    const params = {};
    if (category !== 'all') params.category = category;
    if (gender !== 'all') params.gender = gender;
    if (q) params.q = q;
    api.get('/products', { params })
      .then((r) => setAllProducts(r.data || []))
      .finally(() => setLoading(false));
  }, [category, gender, q]);

  const update = (key, value) => {
    const p = new URLSearchParams(searchParams);
    if (!value || value === 'all') p.delete(key);
    else p.set(key, value);
    if (key === 'category') p.delete('subcategory');
    setSearchParams(p);
  };

  const categoryMap = useMemo(() => Object.fromEntries((categories || []).map((c) => [c.slug, c])), [categories]);
  const activeCat = category !== 'all' ? categoryMap[category] : null;
  const activeSub = activeCat?.subcategories?.find((s) => s.slug === subcategory);
  const allowedGenders = activeCat?.allowed_genders?.length ? activeCat.allowed_genders : null;
  const genderChips = allowedGenders ? ALL_GENDERS.filter((g) => allowedGenders.includes(g.slug)) : ALL_GENDERS;

  // Products filtered further by subcategory (client-side, for grouping)
  const filteredProducts = useMemo(() => {
    if (subcategory === 'all') return allProducts;
    return allProducts.filter((p) => p.subcategory === subcategory);
  }, [allProducts, subcategory]);

  // Should we render as GROUPED VIEW? Yes if a category with subcategories is selected AND we're on "all subcategories"
  const shouldGroup = activeCat && activeCat.subcategories?.length > 0 && subcategory === 'all';

  const heading = activeSub?.name || activeCat?.name || 'Wear the story.';

  return (
    <div className="pt-32 pb-24 px-6 sm:px-10 max-w-[1600px] mx-auto" data-testid="products-page">
      <motion.div
        key={heading}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-10"
      >
        <p className="eyebrow mb-4">
          The Collection
          {activeCat && ` · ${activeCat.name}`}
          {activeSub && ` · ${activeSub.name}`}
        </p>
        <h1 className="font-serif-display text-5xl sm:text-7xl lg:text-8xl tracking-tighter leading-[0.9]">
          <em className="text-[color:var(--mb-primary)] not-italic">{heading}</em>
        </h1>
        {activeCat?.description && !activeSub && (
          <p className="mt-6 max-w-2xl text-lg text-[color:var(--mb-text-muted)] font-light leading-relaxed">
            {activeCat.description}
          </p>
        )}
      </motion.div>

      {/* Category filters */}
      <div className="border-t border-[color:var(--mb-border)] pt-6 pb-4 mb-4">
        <p className="eyebrow mb-3">Category</p>
        <div className="flex flex-wrap gap-2" data-testid="category-filters">
          <button onClick={() => update('category', 'all')} data-testid="filter-all" className={`chip ${category === 'all' ? 'active' : ''}`}>All</button>
          {categories.map((c) => (
            <button key={c.slug} onClick={() => update('category', c.slug)} data-testid={`filter-${c.slug}`} className={`chip ${category === c.slug ? 'active' : ''}`}>
              {c.name}
              {c.subcategories?.length > 0 && (
                <span className={`ml-1.5 text-[0.55rem] ${category === c.slug ? 'text-white/70' : 'text-[color:var(--mb-text-muted)]'}`}>· {c.subcategories.length}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Sub-category filter (optional — grouped view is the default when subs exist) */}
      <AnimatePresence initial={false}>
        {activeCat?.subcategories?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="border-t border-[color:var(--mb-border)] pt-6 pb-4 mb-4">
              <p className="eyebrow mb-3">Sub-category</p>
              <div className="flex flex-wrap gap-2" data-testid="subcategory-filters">
                <button onClick={() => update('subcategory', 'all')} data-testid="sub-filter-all" className={`chip ${subcategory === 'all' ? 'active' : ''}`}>
                  All {activeCat.name}
                </button>
                {activeCat.subcategories.map((s) => (
                  <button key={s.slug} onClick={() => update('subcategory', s.slug)} data-testid={`sub-filter-${s.slug}`} className={`chip ${subcategory === s.slug ? 'active' : ''}`}>
                    {s.name}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Gender + search */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 border-t border-[color:var(--mb-border)] py-6 mb-12">
        <div>
          <p className="eyebrow mb-3">For</p>
          <div className="flex flex-wrap gap-2" data-testid="gender-filters">
            <button onClick={() => update('gender', 'all')} data-testid="gender-all" className={`chip ${gender === 'all' ? 'active' : ''}`}>All</button>
            {genderChips.map((g) => (
              <button key={g.slug} onClick={() => update('gender', g.slug)} data-testid={`gender-${g.slug}`} className={`chip ${gender === g.slug ? 'active' : ''}`}>
                {g.name}
              </button>
            ))}
          </div>
        </div>
        <div className="relative w-full lg:w-80">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[color:var(--mb-text-muted)]" />
          <input
            data-testid="search-input"
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search Bakhu, Dhaka, Bhutanese…"
            className="w-full pl-11 pr-4 py-3 bg-[color:var(--mb-surface)] border border-[color:var(--mb-border)] rounded-full text-sm placeholder:text-[color:var(--mb-text-muted)] focus:border-[color:var(--mb-primary)] outline-none"
            style={{ transition: 'border-color 250ms ease' }}
          />
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div data-testid="products-loading" className="text-center py-32 text-[color:var(--mb-text-muted)] tracking-[0.3em] uppercase text-xs">Curating…</div>
      ) : shouldGroup ? (
        // Grouped by subcategory
        <div className="flex flex-col gap-16" data-testid="grouped-view">
          {activeCat.subcategories.map((sub) => {
            const items = allProducts.filter((p) => p.subcategory === sub.slug);
            if (items.length === 0) return (
              <section key={sub.slug} data-testid={`group-${sub.slug}`}>
                <div className="flex items-baseline justify-between border-b border-[color:var(--mb-border)] pb-3 mb-6">
                  <h2 className="font-serif-display text-3xl sm:text-4xl tracking-tight">{sub.name}</h2>
                  <span className="text-[0.65rem] tracking-[0.3em] uppercase text-[color:var(--mb-text-muted)]">0 pieces</span>
                </div>
                <p className="text-sm text-[color:var(--mb-text-muted)] italic">No pieces in this sub-category yet.</p>
              </section>
            );
            return (
              <section key={sub.slug} data-testid={`group-${sub.slug}`}>
                <div className="flex items-baseline justify-between border-b border-[color:var(--mb-border)] pb-3 mb-6 flex-wrap gap-2">
                  <div>
                    <p className="text-[0.65rem] tracking-[0.3em] uppercase text-[color:var(--mb-primary)] mb-1">{activeCat.name}</p>
                    <h2 className="font-serif-display text-3xl sm:text-4xl tracking-tight">{sub.name}</h2>
                  </div>
                  <button onClick={() => update('subcategory', sub.slug)} className="text-[0.65rem] tracking-[0.3em] uppercase text-[color:var(--mb-primary)] hover:underline">
                    See only {sub.name} →
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-14">
                  {items.map((p, i) => (
                    <ProductCard product={p} key={p.id} index={i} categoryMap={categoryMap} />
                  ))}
                </div>
              </section>
            );
          })}
          {/* Uncategorised (no subcategory) */}
          {(() => {
            const orphans = allProducts.filter((p) => !p.subcategory);
            if (orphans.length === 0) return null;
            return (
              <section data-testid="group-uncategorised">
                <div className="flex items-baseline justify-between border-b border-[color:var(--mb-border)] pb-3 mb-6">
                  <h2 className="font-serif-display text-3xl sm:text-4xl tracking-tight">Other {activeCat.name}</h2>
                  <span className="text-[0.65rem] tracking-[0.3em] uppercase text-[color:var(--mb-text-muted)]">{orphans.length} piece{orphans.length > 1 ? 's' : ''}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-14">
                  {orphans.map((p, i) => (
                    <ProductCard product={p} key={p.id} index={i} categoryMap={categoryMap} />
                  ))}
                </div>
              </section>
            );
          })()}
        </div>
      ) : filteredProducts.length === 0 ? (
        <div data-testid="products-empty" className="text-center py-32">
          <p className="font-serif-display text-3xl">No pieces found in this filter.</p>
          <p className="mt-3 text-[color:var(--mb-text-muted)] text-sm">Try another category, or contact us on WhatsApp for a custom order.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-14">
          {filteredProducts.map((p, i) => (
            <ProductCard product={p} key={p.id} index={i} categoryMap={categoryMap} />
          ))}
        </div>
      )}
    </div>
  );
}
